/**
 * Turning a series of dated readings into something a chart can draw **without lying about it.**
 *
 * Two workspace rules govern every chart in this app, and both are decisions this module makes so
 * that no component has to:
 *
 *   1. **The x axis is real time, never an index.** A row carries its `Date`, and the renderer
 *      places it by instant. Fourteen days between two readings must look like fourteen days.
 *   2. **A missing reading is a BREAK, never a zero.** A trainer who did not weigh in did not
 *      weigh 0 kg, and a line drawn straight across the hole reports a fortnight of progress
 *      nobody measured.
 *
 * ── This used to cut the series into runs, and no longer does ─────────────────────────────────
 * Until 31 August 2026 the vendored bklit renderer could not express a gap at all: it read a point
 * as `typeof y === "number" ? yScale(y) ?? 0 : 0`, so a null became **pixel 0, the top of the
 * plot** — a hole rendered as a record high. This module worked around that by cutting the series
 * at every hole and handing back contiguous runs for the caller to draw one at a time.
 *
 * That renderer defect is now fixed at its source (`shared/vendor/bklit/area.tsx` and
 * `series-path-utils.ts` both carry a `defined` predicate; `shared/vendor/AGENTS.md` records the
 * patch and warns that a re-vendor deletes it). So the workaround is gone and the honest shape is
 * back: **one ordered series, holes kept as `null`,** which is what `shared/ui/chart/ReadoutChart`
 * hands straight to bklit. Nothing here filters a hole out — a filtered array closes the gap
 * silently and draws a straight line through a fortnight nobody reported, which is the failure this
 * whole module exists to prevent.
 *
 * ── The y domain is a decision, not a default ────────────────────────────────────────────────
 * bklit anchors every all-positive series at zero. Zero is the honest baseline for a COUNT — sets,
 * tonnage, sessions — where the quantity has a real zero and the mark's length is the figure. It is
 * not the baseline for a narrow band around a large number: bodyweight 79–82 kg on an axis of 0–91
 * is a flat line, and a picture that only says "the number is large" repeats the figure printed
 * above it and shows nothing else. `fittedYDomain` answers the second case; a caller that wants the
 * first simply does not call it. The honesty condition travels with it and belongs to the caller: a
 * fitted axis is only honest while its ticks print their real values.
 */

/**
 * One dated reading, in the shape a chart plots it: an instant and a value.
 *
 * `null` means "not reported", and is the only way to say that — never a zero, which is the whole
 * doctrine above.
 *
 * **The one point shape in this app.** It was five: `Reading` here, `ReadoutPoint` on the chart,
 * `SummaryPoint` in `chartSummary`, `SeriesPoint` in `pages/client-detail/model/trends`, plus the
 * two non-nullable ones the records and measurements projections still keep because their value
 * genuinely cannot be missing. Four of the five were the same fields under four names, and one of
 * them spelled them `at`/`value` — which bought a `useMemo` in `ReadoutChart` whose only work was
 * to rename two properties, and identity `.map()`s at two call sites to get past it.
 */
export interface ChartPoint {
  readonly x: Date;
  readonly y: number | null;
}

/**
 * A row in the shape the chart consumes: the instant, plus the series value under its key.
 * `null` under the value key is a HOLE, and the renderer draws a break across it.
 */
export type ChartRow = Record<string, Date | number | null>;

export interface ChartSeries {
  /**
   * Every reading in time order, holes included, trimmed to the span that was actually reported.
   * Empty when nothing was reported at all.
   */
  readonly rows: readonly ChartRow[];
  /**
   * The instants the series spans, for the caller to pin the x scale to. `null` when there is
   * nothing to draw — an absent domain is the caller's cue to render an empty state rather than a
   * chart of one invented point.
   */
  readonly xDomain: readonly [Date, Date] | null;
  /** How many reported readings survived. */
  readonly readingCount: number;
  /** Whether at least one hole sits INSIDE the drawn span, so a caller can say so in words. */
  readonly hasBreak: boolean;
}

/** The key a chart row stores its value under, and the key the renderer reads. */
export const SERIES_VALUE_KEY = 'value';
/** The key a chart row stores its instant under. */
export const SERIES_DATE_KEY = 'date';

/**
 * A number a scale can actually place.
 *
 * `Number.isFinite` rather than `!= null`: a NaN or an Infinity survives a null check, reaches the
 * scale, and produces a path with `NaN` coordinates — which renders as nothing at all, with no
 * error anywhere. That is worse than a hole, because a hole is visible.
 *
 * Exported because a caller that decides a SHARED x domain has to agree with the trimming below
 * about which points count. `pages/client-detail`'s `trends` tested `y === null` on its own and a
 * `NaN` therefore widened the span the three charts share while being a hole in every one of them
 * — three plots on an axis wider than any of them has data for.
 */
export function isReported(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

/**
 * Project a reading series into drawable rows.
 *
 * Readings arrive in whatever order the caller had them and are sorted here, because a chart that
 * trusts input order draws a zig-zag when the API returns newest-first and nobody notices until a
 * trainer does. Sorting is stable on equal instants, so two readings at the same moment keep the
 * order they were given rather than swapping between renders.
 *
 * **Leading and trailing holes are trimmed**, and holes inside the span are not. A null before the
 * first reading and after the last one carries no information the chart can draw — it would only
 * stretch the x domain over dates nobody reported on, making the whole series look narrower than it
 * is. A null BETWEEN two readings is the information.
 */
export function toChartSeries(readings: readonly ChartPoint[]): ChartSeries {
  const ordered = [...readings].sort((a, b) => a.x.getTime() - b.x.getTime());

  const first = ordered.findIndex((reading) => isReported(reading.y));
  if (first === -1) return { rows: [], xDomain: null, readingCount: 0, hasBreak: false };

  let last = ordered.length - 1;
  while (last > first && !isReported(ordered[last]?.y ?? null)) last -= 1;

  const span = ordered.slice(first, last + 1);
  const rows: ChartRow[] = span.map((reading) => ({
    [SERIES_DATE_KEY]: reading.x,
    // A hole is written as `null`, never omitted and never defaulted. The renderer's `defined`
    // predicate reads exactly this.
    [SERIES_VALUE_KEY]: isReported(reading.y) ? reading.y : null,
  }));

  const readingCount = span.filter((reading) => isReported(reading.y)).length;
  // Both ends are reported readings by construction — `first` found one and the `while` above
  // walked `last` back onto one — so the span's edges are the domain and neither is a hole.
  const from = span[0]?.x;
  const to = span[span.length - 1]?.x;

  return {
    rows,
    xDomain: from === undefined || to === undefined ? null : ([from, to] as const),
    readingCount,
    hasBreak: readingCount < span.length,
  };
}

/** How much of the data's own span to leave above and below it. KANON's own figure. */
const HEADROOM = 0.18;

/**
 * The y extent for a quantity whose ZERO IS NOT A BASELINE — bodyweight, sleep, a one-rep max.
 *
 * Returns `null` when nothing was reported, which is the caller's cue to draw no chart rather than
 * an axis over an empty range. A series whose readings are all the same value still gets a band:
 * a zero-height domain collapses every point onto one pixel row and the scale divides by zero.
 *
 * This is deliberately NOT applied to counts. See the module header.
 */
export function fittedYDomain(
  readings: readonly ChartPoint[],
): readonly [number, number] | null {
  const values = readings.map((reading) => reading.y).filter(isReported);
  if (values.length === 0) return null;

  const low = Math.min(...values);
  const high = Math.max(...values);
  // A flat series pads by a fraction of its own magnitude rather than by a constant, so the band
  // reads the same on 5 kg and on 500 kg. `|| 1` covers a flat series sitting exactly on zero.
  const pad = (high - low) * HEADROOM || Math.abs(high) * HEADROOM || 1;

  return [low - pad, high + pad] as const;
}

/** The most decimals a gutter figure needs; past it the step is float noise, not a digit. */
const MAX_TICK_DIGITS = 6;

/**
 * The decimals a set of axis ticks needs so that no two print alike: as many as their STEP has.
 * A 0.05 t step needs two, so 0,75 and 0,8 stop both reading „0,8". Never fewer than `floor`.
 */
export function tickFractionDigits(ticks: readonly number[], floor = 0): number {
  const [first, second] = ticks;
  if (first === undefined || second === undefined) return floor;
  const step = Math.abs(second - first);
  if (!Number.isFinite(step) || step === 0) return floor;
  for (let digits = floor; digits < MAX_TICK_DIGITS; digits += 1) {
    const scaled = step * 10 ** digits;
    if (Math.abs(scaled - Math.round(scaled)) < 1e-6 * Math.max(1, scaled)) return digits;
  }
  return MAX_TICK_DIGITS;
}

/** The steps a time axis stands on, in seconds: a clock's own round numbers. */
const TIME_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 18_000, 36_000];

/**
 * Ticks for an axis of seconds, on the first clock step that gives at most `count` intervals over
 * the domain (either way round). Whole seconds only, never below zero, so a clock never prints two
 * labels alike, which decimal ticks do the moment a fitted pace axis spans ten seconds.
 */
export function timeTicks(domain: readonly [number, number], count: number): readonly number[] {
  return ticksOnSteps(domain, count, TIME_STEPS);
}

/** The steps a count stands on: 1, 2 and 5 times each power of ten, up to a hundred million. */
const COUNT_STEPS = Array.from({ length: 9 }, (_, power) => [1, 2, 5].map((base) => base * 10 ** power)).flat();

/**
 * Ticks for an axis of whole things — people, visits — on the first 1-2-5 step that gives at most
 * `count` intervals. Whole numbers only, never below zero: a count has no „1,5 osoby".
 */
export function countTicks(domain: readonly [number, number], count: number): readonly number[] {
  return ticksOnSteps(domain, count, COUNT_STEPS);
}

function ticksOnSteps(domain: readonly [number, number], count: number, steps: readonly number[]): readonly number[] {
  const low = Math.max(0, Math.min(domain[0], domain[1]));
  const high = Math.max(domain[0], domain[1]);
  const span = high - low;
  if (!(span > 0) || count < 1) return [];
  const step = steps.find((candidate) => span / candidate <= count) ?? steps[steps.length - 1] ?? 1;
  const ticks: number[] = [];
  for (let tick = Math.ceil(low / step) * step; tick <= high; tick += step) ticks.push(tick);
  return ticks;
}

/** Below this many days the weekly labels step down to whole days. */
const SHORT_AXIS_DAYS = 21;
/** About how many day labels a short axis carries. */
const SHORT_AXIS_LABELS = 5;
const DAY_MS = 86_400_000;
/** The hour a day label stands at: the middle of the day it names. */
const DAY_LABEL_HOUR = 12;

/**
 * The day-and-month x labels strictly inside `from`–`to`: weekly, stepping down to whole days on a
 * history shorter than three weeks (about five labels), each at LOCAL NOON.
 *
 * Noon, because a label names a whole day and its middle is where that day's readings sit around.
 * At midnight a session logged at 20:00 on 4 October stood four hours from the „5 paź" label and
 * twenty from „4 paź", so it read as the 5th. Calendar arithmetic (`setDate`), never `+ 7 × 24 h`:
 * a week that crosses the October clock change is 169 hours long, and a fixed step lands the label
 * on the evening before the day it names.
 */
export function dayTicks(from: Date, to: Date): readonly Date[] {
  const days = (to.getTime() - from.getTime()) / DAY_MS;
  const step = days >= SHORT_AXIS_DAYS ? 7 : Math.max(1, Math.ceil(days / SHORT_AXIS_LABELS));
  const ticks: Date[] = [];
  const cursor = new Date(from);
  cursor.setHours(DAY_LABEL_HOUR, 0, 0, 0);
  cursor.setDate(cursor.getDate() + (step === 7 ? 7 : 1));
  while (cursor.getTime() < to.getTime()) {
    ticks.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + step);
  }
  return ticks;
}
